import { useMarkSubCategoryAsCompleted } from "@/api/app/assessment/useMarkSubCategoryAsCompleted";
import { useSaveChanges } from "@/api/app/assessment/useSaveChanges";
import toast from "@/lib/toast";
import { AnswerSubCategoryDetail } from "@/schema/app/assessment/answerSubCategoryDetail";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

interface UseSubCategoryActionsOptions {
  subCategoryId: string;
  refetch: () => Promise<unknown> | void;
}

export const useSubCategoryActions = ({
  subCategoryId,
  refetch,
}: UseSubCategoryActionsOptions) => {
  const { t } = useTranslation();
  const { handleSubmit, reset } = useFormContext<AnswerSubCategoryDetail>();

  const { trigger: saveChanges, isMutating: isSaving } = useSaveChanges(
    subCategoryId,
  );

  const { trigger: markAsCompleted, isMutating: isMarking } =
    useMarkSubCategoryAsCompleted(subCategoryId, {
      onSuccess: (data) => {
        toast.success(data.message);
        refetch();
      },
    });

  const buildPayload = (data: AnswerSubCategoryDetail) => ({
    answer_contents: data.answer_contents.map(({ content, ...rest }) => rest),
  });

  const saveDraft = () =>
    new Promise<boolean>((resolve) => {
      handleSubmit(
        async (data) => {
          try {
            toast.loading(t("save-draft-loading"));
            const response = await saveChanges(buildPayload(data));
            toast.success(response.message);
            await Promise.resolve(refetch());
            reset(data, { keepValues: true });
            resolve(true);
          } catch (error) {
            resolve(false);
          }
        },
        () => resolve(false),
      )();
    });

  const markCompleted = () =>
    new Promise<boolean>((resolve) => {
      handleSubmit(
        async (data) => {
          try {
            toast.loading(t("mark-as-completed-loading"));
            await saveChanges(buildPayload(data));
            await markAsCompleted();
            await Promise.resolve(refetch());
            reset(data, { keepValues: true });
            resolve(true);
          } catch (error) {
            resolve(false);
          }
        },
        () => resolve(false),
      )();
    });

  return {
    saveDraft,
    markCompleted,
    isSaving,
    isMarking,
  };
};